import React, { useEffect, useState, useCallback, useMemo } from 'react';
import '../../../../styles/Events.css';
import '../../../../styles/Pages.css';

const API = `${process.env.REACT_APP_API_URL}/api`;

/* ────────────────────────────────────────────────────────────
   AdminPollsSection
   Embedded inside AdminDashboard — NO Navbar / Footer.
   Shows:
     • Create Poll form (comma-separated options, time limits)
     • Poll list with status badges, quick actions
     • Inline Results panel
────────────────────────────────────────────────────────────── */
function AdminPollsSection() {
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [viewResultsId, setViewResultsId] = useState(null);
    const [resultsData, setResultsData] = useState(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [form, setForm] = useState({
        title: '', description: '', category: 'Farewell',
        optionNames: '', endTime: '', startTime: '',
    });
    const [formMsg, setFormMsg] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

    /* ── Fetch all polls ── */
    const fetchPolls = useCallback(() => {
        setLoading(true);
        fetch(`${API}/admin/polls`, { headers })
            .then(r => r.json())
            .then(data => {
                if (data.success) setPolls(data.polls);
                else setError(data.message);
            })
            .catch(() => setError('Failed to load polls.'))
            .finally(() => setLoading(false));
    }, [headers]);

    useEffect(() => { fetchPolls(); }, [fetchPolls]);

    /* ── Create poll ── */
    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true); setFormMsg('');
        try {
            const res = await fetch(`${API}/admin/polls`, {
                method: 'POST', headers,
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                setFormMsg('✅ Poll created successfully!');
                setShowForm(false);
                setForm({ title: '', description: '', category: 'Farewell', optionNames: '', endTime: '', startTime: '' });
                fetchPolls();
            } else {
                setFormMsg(`❌ ${data.message}`);
            }
        } catch { setFormMsg('❌ Network error.'); }
        finally { setSubmitting(false); }
    };

    /* ── Toggle active ── */
    const toggleActive = async (poll) => {
        await fetch(`${API}/admin/polls/${poll._id}`, {
            method: 'PUT', headers,
            body: JSON.stringify({ isActive: !poll.isActive }),
        });
        fetchPolls();
    };

    /* ── Delete poll ── */
    const deletePoll = async (id) => {
        if (!window.confirm('Delete this poll and ALL its votes? This cannot be undone.')) return;
        await fetch(`${API}/admin/polls/${id}`, { method: 'DELETE', headers });
        if (viewResultsId === id) { setViewResultsId(null); setResultsData(null); }
        fetchPolls();
    };

    /* ── Reset votes ── */
    const resetVotes = async (id) => {
        if (!window.confirm('Reset all votes for this poll? This cannot be undone.')) return;
        await fetch(`${API}/admin/polls/${id}/reset`, { method: 'POST', headers });
        fetchPolls();
        if (viewResultsId === id) loadResults(id);
    };

    /* ── Load results inline ── */
    const loadResults = (id) => {
        setViewResultsId(id);
        setResultsLoading(true);
        fetch(`${API}/admin/polls/${id}/results`, { headers })
            .then(r => r.json())
            .then(d => { if (d.success) setResultsData(d); })
            .catch(() => { })
            .finally(() => setResultsLoading(false));
    };

    const closeResults = () => { setViewResultsId(null); setResultsData(null); };

    /* ── Status helper ── */
    const getStatus = (poll) => {
        const now = new Date();
        if (!poll.isActive) return { label: '⚪ Inactive', cls: 'inactive' };
        if (new Date(poll.endTime) < now) return { label: '🔴 Ended', cls: 'ended' };
        if (new Date(poll.startTime) > now) return { label: '🟡 Scheduled', cls: 'scheduled' };
        return { label: '🟢 Live', cls: 'live' };
    };

    /* ── Options preview ── */
    const optionPreviews = form.optionNames
        ? form.optionNames.split(',').map(n => n.trim()).filter(Boolean)
        : [];

    return (
        <div className="dash-polls-section">

            {/* ── Header ── */}
            <div className="dash-polls-topbar">
                <div>
                    <h1 className="dash-polls-title">🗳️ Poll Management</h1>
                    <p className="dash-polls-subtitle">Create & manage farewell award polls — no login required to vote.</p>
                </div>
                <button
                    className={`create-poll-btn ${showForm ? 'cancel-mode' : ''}`}
                    onClick={() => { setShowForm(!showForm); setFormMsg(''); }}
                >
                    {showForm ? '✕ Cancel' : '+ New Poll'}
                </button>
            </div>

            {/* ── Form message ── */}
            {formMsg && (
                <div className={`poll-msg ${formMsg.startsWith('✅') ? 'success' : 'error'}`}>
                    {formMsg}
                </div>
            )}

            {/* ── Create Poll Form ── */}
            {showForm && (
                <div className="admin-poll-form-box">
                    <h3>🆕 Create a New Poll</h3>
                    <form onSubmit={handleCreate}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Poll Title *</label>
                                <input
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                    placeholder="e.g. Best Attitude Award — Farewell 2025"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <input
                                    value={form.category}
                                    onChange={e => setForm({ ...form, category: e.target.value })}
                                    placeholder="e.g. Farewell, Sports, Academic"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Description (optional)</label>
                            <input
                                value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                                placeholder="Short description shown to voters"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Candidate / Option Names *{' '}
                                <small style={{ color: '#666', fontWeight: 400 }}>
                                    (comma-separated — e.g.: Rahul, Priya, Amit, Sneha)
                                </small>
                            </label>
                            <textarea
                                value={form.optionNames}
                                onChange={e => setForm({ ...form, optionNames: e.target.value })}
                                placeholder="Rahul Sharma, Priya Patel, Amit Kumar, Sneha Jadhav, Rohan Patil"
                                rows={3}
                                required
                            />
                            {optionPreviews.length > 0 && (
                                <div className="names-preview">
                                    <strong style={{ fontSize: '0.82rem', color: '#1d3b1d' }}>
                                        {optionPreviews.length} option{optionPreviews.length !== 1 ? 's' : ''} detected:
                                    </strong>{' '}
                                    {optionPreviews.map((n, i) => (
                                        <span key={i} className="name-chip">
                                            {i + 1}. {n}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Start Time <small style={{ color: '#888', fontWeight: 400 }}>(leave blank = now)</small></label>
                                <input
                                    type="datetime-local"
                                    value={form.startTime}
                                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>End Time / Deadline *</label>
                                <input
                                    type="datetime-local"
                                    value={form.endTime}
                                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <button type="submit" className="submit-btn" disabled={submitting}>
                            {submitting ? 'Creating…' : '🗳️ Create Poll'}
                        </button>
                    </form>
                </div>
            )}

            {/* ── Poll List ── */}
            <div className="dash-polls-summary">
                <span>{polls.length} poll{polls.length !== 1 ? 's' : ''} total</span>
                <span className="dot-sep">•</span>
                <span style={{ color: '#2e7d32' }}>
                    {polls.filter(p => {
                        const now = new Date();
                        return p.isActive && new Date(p.endTime) >= now && new Date(p.startTime) <= now;
                    }).length} live
                </span>
                <span className="dot-sep">•</span>
                <span style={{ color: '#e91e63' }}>
                    {polls.filter(p => new Date(p.endTime) < new Date()).length} ended
                </span>
            </div>

            {loading && (
                <div className="events-loading">
                    <div className="events-spinner" />
                    <p>Loading polls…</p>
                </div>
            )}
            {error && <div className="events-error">{error}</div>}
            {!loading && polls.length === 0 && (
                <div className="events-empty">
                    <span>🗳️</span>
                    <h3>No polls yet</h3>
                    <p>Click "New Poll" to create your first farewell award poll.</p>
                </div>
            )}

            <div className="admin-polls-list">
                {polls.map(poll => {
                    const { label, cls } = getStatus(poll);
                    const totalVotes = poll.options.reduce((s, o) => s + o.votes, 0);
                    const isShowingResults = viewResultsId === poll._id;

                    return (
                        <div key={poll._id} className={`admin-poll-card status-${cls}`}>
                            {/* Card top */}
                            <div className="apc-top">
                                <div style={{ flex: 1 }}>
                                    <span className={`poll-status-badge ${cls}`}>{label}</span>
                                    <h3 className="apc-title">{poll.title}</h3>
                                    <p className="apc-meta">
                                        {poll.category} &bull; {poll.options.length} candidates &bull;{' '}
                                        <strong style={{ color: '#1d3b1d' }}>{totalVotes}</strong> total votes
                                    </p>
                                    <p className="apc-time">
                                        🕐 Starts: {new Date(poll.startTime).toLocaleString('en-IN')}
                                        &nbsp;&nbsp;|&nbsp;&nbsp;
                                        ⏰ Ends: {new Date(poll.endTime).toLocaleString('en-IN')}
                                    </p>
                                </div>
                                <div className="apc-actions">
                                    <button
                                        onClick={() => isShowingResults ? closeResults() : loadResults(poll._id)}
                                        className={`apc-btn results`}
                                    >
                                        {isShowingResults ? '✕ Hide' : '📊 Results'}
                                    </button>
                                    <button
                                        onClick={() => toggleActive(poll)}
                                        className={`apc-btn ${poll.isActive ? 'deactivate' : 'activate'}`}
                                    >
                                        {poll.isActive ? '⏸ Deactivate' : '▶ Activate'}
                                    </button>
                                    <button onClick={() => resetVotes(poll._id)} className="apc-btn reset">
                                        🔄 Reset
                                    </button>
                                    <button onClick={() => deletePoll(poll._id)} className="apc-btn delete">
                                        🗑 Delete
                                    </button>
                                </div>
                            </div>

                            {/* Options strip */}
                            <div className="apc-options-strip">
                                {poll.options.slice(0, 10).map((o, i) => (
                                    <span key={i} className="apc-option-chip">{o.label}</span>
                                ))}
                                {poll.options.length > 10 && (
                                    <span className="apc-option-chip more">+{poll.options.length - 10} more</span>
                                )}
                            </div>

                            {/* ── Inline Results Panel ── */}
                            {isShowingResults && (
                                <div className="dash-results-panel">
                                    {resultsLoading ? (
                                        <div className="events-loading" style={{ minHeight: '120px' }}>
                                            <div className="events-spinner" />
                                            <p>Loading results…</p>
                                        </div>
                                    ) : resultsData ? (
                                        <>
                                            {/* Winner card */}
                                            {resultsData.totalVotes > 0 && (() => {
                                                const sorted = [...resultsData.poll.options]
                                                    .sort((a, b) => b.votes - a.votes);
                                                const winner = sorted[0];
                                                const pct = Math.round((winner.votes / resultsData.totalVotes) * 100);
                                                return (
                                                    <div className="dash-winner-strip">
                                                        <span className="dash-trophy">🏆</span>
                                                        <div>
                                                            <div className="dash-winner-label">Current Leader</div>
                                                            <div className="dash-winner-name">{winner.label}</div>
                                                        </div>
                                                        <div className="dash-winner-stats">
                                                            <span className="dash-winner-votes">{winner.votes} votes</span>
                                                            <span className="dash-winner-pct">{pct}%</span>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Bar chart */}
                                            <div className="dash-results-bars">
                                                {[...resultsData.poll.options]
                                                    .map((o, i) => ({ ...o, idx: i }))
                                                    .sort((a, b) => b.votes - a.votes)
                                                    .map((opt, rank) => {
                                                        const pct = resultsData.totalVotes > 0
                                                            ? Math.round((opt.votes / resultsData.totalVotes) * 100)
                                                            : 0;
                                                        return (
                                                            <div key={opt.idx} className="dash-result-row">
                                                                <span className="dash-rank">#{rank + 1}</span>
                                                                <span className="dash-cand-name">{opt.label}</span>
                                                                <div className="dash-bar-wrap">
                                                                    <div
                                                                        className={`dash-bar-fill ${rank === 0 && opt.votes > 0 ? 'winner' : ''}`}
                                                                        style={{ width: `${pct}%` }}
                                                                    />
                                                                </div>
                                                                <span className="dash-vote-count">
                                                                    <strong>{opt.votes}</strong> <span>({pct}%)</span>
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                            {resultsData.totalVotes === 0 && (
                                                <p style={{ color: '#888', textAlign: 'center', padding: '1rem' }}>
                                                    No votes recorded yet.
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p style={{ color: '#888', textAlign: 'center', padding: '1rem' }}>
                                            Could not load results.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default AdminPollsSection;

